import {
  isEmbeddedInSucceed,
  isEmbeddedInHost,
  isEmbeddedInFCUI,
  isQaSucceedApiProxyDev,
  getDistrictId,
  getHighschoolId,
} from './cp-context';

/**
 * Returns true when calling course-planner-service directly (local dev).
 * When embedded in any host (Succeed staff or FCUI student), or when
 * VITE_QA_SUCCEED_API=true, use /course-planner/* proxied paths.
 */
export function isDirectApiMode(): boolean {
  if (isQaSucceedApiProxyDev()) {
    return false;
  }
  return !isEmbeddedInHost();
}

/**
 * Returns the proxy prefix for the current host.
 * Succeed staff host: `/course-planner`
 * FCUI student host: `/course-planner-next` (same as legacy CPUI student side)
 */
function getProxyPrefix(): string {
  const isQaStudentDev = isQaSucceedApiProxyDev() && import.meta.env.VITE_APP_TARGET === 'student';
  return isEmbeddedInFCUI() || isQaStudentDev ? '/course-planner-next' : '/course-planner';
}

/**
 * Generic path builder for course-planner-service resources.
 * Use for subject-areas, config, recommendations - same pattern for all.
 *
 * @param resource - e.g. 'subject-areas', 'config', 'recommendations'
 * @param action - optional suffix, e.g. 'delete', 'update'
 */
export function getCoursePlannerPath(resource: string, action?: string): string {
  if (isDirectApiMode()) {
    const districtId = getDistrictId();
    const highschoolId = getHighschoolId();
    const base = `/${resource}/district/${districtId}/highschool/${highschoolId}`;
    return action ? `${base}/${action}` : base;
  }
  const base = `${getProxyPrefix()}/${resource}`;
  return action ? `${base}/${action}` : base;
}

/**
 * Path for resources that have no district/highschool segment in direct mode.
 * Embedded: /course-planner/{resource}/{action}. Direct: /{resource}/{action}.
 * Use for lookup-data and any future unscoped resources.
 */
export function getSimplePath(resource: string, action?: string): string {
  if (isDirectApiMode()) {
    return action ? `/${resource}/${action}` : `/${resource}`;
  }
  const base = `${getProxyPrefix()}/${resource}`;
  return action ? `${base}/${action}` : base;
}

/**
 * Base path for subject-areas resource.
 */
export function getSubjectAreasBasePath(): string {
  return getCoursePlannerPath('subject-areas');
}

/**
 * Path for subject-areas actions (delete, update).
 */
export function getSubjectAreasPath(action: 'delete' | 'update'): string {
  return getCoursePlannerPath('subject-areas', action);
}

/**
 * Base path for plans-of-study resource.
 */
export function getPlansOfStudyBasePath(): string {
  return getCoursePlannerPath('plans-of-study');
}

/**
 * Path for plans-of-study actions (e.g. all-lite for list, copy).
 * For create, use base path with POST.
 */
export function getPlansOfStudyPath(
  action: 'all-lite' | 'all' | 'copy' | 'list' | 'publish' | 'active-schools'
): string {
  if (action === 'copy') {
    return getCoursePlannerPath('plans-of-study', 'copy');
  }
  if (action === 'publish') {
    return getCoursePlannerPath('plans-of-study', 'publish');
  }
  if (action === 'active-schools') {
    return getCoursePlannerPath('plans-of-study', 'active-schools');
  }
  if (action === 'list') {
    return `${getPlansOfStudyBasePath()}/list`;
  }
  if (action === 'all-lite') {
    return `${getPlansOfStudyBasePath()}/all/lite`;
  }
  return `${getPlansOfStudyBasePath()}/all`;
}

/**
 * Path for a single plan of study (GET/PUT/PATCH/DELETE .../plans-of-study/:id).
 */
export function getPlansOfStudyDetailPath(id: string): string {
  return `${getPlansOfStudyBasePath()}/${id}`;
}

/**
 * Path for GET/PUT config.
 */
export function getCoursePlannerConfigPath(): string {
  return getCoursePlannerPath('config');
}

/**
 * Path for PUT/POST config/school-cluster-guide (Additional resources).
 * The real backend saves schoolClusterGuide via this dedicated endpoint only —
 * PUT /config ignores schoolClusterGuide fields.
 *
 * Embedded (QA/prod): POST /config/school-cluster-guide → succeed-api handles
 * S3 upload + proxies metadata to course-planner-service as PUT.
 * Direct (local): PUT /config/district/.../highschool/.../school-cluster-guide.
 */
export function getCoursePlannerSchoolClusterGuidePath(): string {
  return `${getCoursePlannerPath('config')}/school-cluster-guide`;
}

/**
 * Path for POST /config/school-cluster-guide sent directly to succeed-api
 * (embedded/QA/prod mode only). succeed-api's ConfigController is mounted at
 * /config — NOT under /course-planner/ — so this must NOT use the proxy prefix.
 * Matches the old cp-ui: UPLOAD_FILE_URL = '/config/school-cluster-guide'.
 */
export function getSucceedApiSchoolClusterGuidePath(): string {
  return '/config/school-cluster-guide';
}

/**
 * Path for GET /config/upload-url sent directly to succeed-api (embedded/QA/prod only).
 * Returns a presigned S3 URL the browser PUTs the file bytes to. succeed-api's
 * ConfigController is mounted at /config — NOT under /course-planner/ — so this must
 * NOT use the proxy prefix. Matches legacy cp-ui: CONFIG_UPLOAD_URL = '/config/upload-url'.
 */
export function getSucceedApiConfigUploadUrlPath(): string {
  return '/config/upload-url';
}

/**
 * Path for downloading the school cluster guide binary file.
 *
 * Matches legacy CPUI student behavior:
 * - Embedded in FCUI: /course-planner-next/school-cluster-guide-file
 * - Embedded in staff host: /course-planner/school-cluster-guide-file
 * - Direct/local: /school-cluster-guide-file
 */
export function getSchoolClusterGuideFileDownloadPath(): string {
  return getSimplePath('school-cluster-guide-file');
}

/**
 * Path for lookup-data (instructional-levels, credit-types, etc.).
 * Uses getSimplePath (no district/highschool segment in direct mode).
 */
export function getLookupDataPath(action: string): string {
  return getSimplePath('lookup-data', action);
}

/** Maximum number of clusters to fetch per school — schools won't exceed this in practice. */
const CLUSTERS_FOR_SCHOOL_PAGE_SIZE = 999;

/**
 * Path for GET clusters/findByActiveSchool.
 * Returns Areas (clusters) with nested active Specializations (pathways) published to a school.
 * Local direct mode: GET /clusters/district/{d}/highschool/{h}/findByActiveSchool?activeSchool={schoolId}&pageSize=999
 * Embedded/QA student: GET /course-planner-next/clusters/findByActiveSchool?activeSchool={schoolId}&pageSize=999
 * Embedded/QA staff: GET /course-planner/clusters/findByActiveSchool?activeSchool={schoolId}&pageSize=999
 */
export function getClustersForSchoolPath(schoolId: string): string {
  const base = getCoursePlannerPath('clusters', 'findByActiveSchool');
  return `${base}?activeSchool=${encodeURIComponent(schoolId)}&pageSize=${CLUSTERS_FOR_SCHOOL_PAGE_SIZE}`;
}

/**
 * Path for GET school student groups (for plan of study "Student groups" / "Student Group Restrictions").
 * Must match CPUI exactly: CPUI calls GET /school-student-groups (no /course-planner/ prefix).
 * Succeed API exposes this at the root path, not under /course-planner/.
 */
export function getSchoolStudentGroupsPath(): string {
  if (isEmbeddedInSucceed() || isQaSucceedApiProxyDev()) {
    return '/school-student-groups';
  }
  return getCoursePlannerPath('school-student-groups');
}

/**
 * Path for GET school student groups scoped to the current highschool.
 * Must match CPUI exactly: CPUI calls GET /school-student-groups/highschoolStudentGroups
 * (no /course-planner/ prefix). Succeed API exposes this at the root path, not under /course-planner/.
 */
export function getHighSchoolStudentGroupsPath(): string {
  if (isEmbeddedInSucceed() || isQaSucceedApiProxyDev()) {
    return '/school-student-groups/highschoolStudentGroups';
  }
  return getCoursePlannerPath('school-student-groups', 'highschoolStudentGroups');
}

/**
 * Path for getCourses in QA (embedded in Succeed). Must match old c-p-ui exactly:
 * POST {API_URL}/course-planner/courses/getCourses → succeed-api → course-planner-service.
 * Do not change this string; local mode uses a different path via getCoursePlannerPath.
 */
export function getCoursePlannerGetCoursesPathQa(): string {
  return `${getProxyPrefix()}/courses/getCoursesModern`;
}

/**
 * Base path for courses resource (course catalog).
 * QA: /course-planner/courses (same as old UI). Local: /courses/district/{d}/highschool/{h}.
 */
export function getCoursesBasePath(): string {
  return getCoursePlannerPath('courses');
}

/**
 * Path for courses actions (getCourses, getCoursesModern, bulk/delete, etc.).
 * In QA this returns /course-planner/courses/getCourses for 'getCourses' (same as old c-p-ui).
 */
export function getCoursesPath(
  action:
    | 'getCourses'
    | 'getCoursesModern'
    | 'bulk/delete'
    | 'bulk/export'
    | 'bulk/exportSchool'
    | 'bulk/export-course-mapping'
    | 'bulk/export-course-mapping-V1'
    | 'bulk/status'
    | 'bulk/subjectArea'
): string {
  return `${getCoursesBasePath()}/${action}`;
}

/**
 * GET course ID availability (same as legacy c-p-ui: `/course-planner/courses/:id/exists`).
 * Returns whether a course with that ID already exists in the namespace.
 */
export function getCourseExistsPath(courseId: string): string {
  const encoded = encodeURIComponent(courseId);
  return `${getCoursesBasePath()}/${encoded}/exists`;
}

/**
 * Base path for clusters (specialization areas) list and CRUD.
 * Embedded: GET /course-planner/clusters?page=&pageSize=&search=...
 * Direct: GET /clusters/district/.../highschool/... (same pattern as other scoped resources).
 */
export function getClustersBasePath(): string {
  return getCoursePlannerPath('clusters');
}
export function getClustersV2Path(): string {
  if (isDirectApiMode()) {
    const districtId = getDistrictId();
    const highschoolId = getHighschoolId();
    return `/clusters/v2/district/${districtId}/highschool/${highschoolId}`;
  }
  return `${getProxyPrefix()}/clusters/v2`;
}

/**
 * Publish cluster (specialization area) to schools.
 * Embedded: PUT /course-planner/clusters/publish
 * Direct: PUT /clusters/district/{d}/highschool/{h}/publish
 */
export function getClustersPublishPath(): string {
  return getCoursePlannerPath('clusters', 'publish');
}

/** Pathways (specializations within a cluster). Legacy staff: `POST/GET /course-planner/pathways`. */
export function getPathwaysBasePath(): string {
  return getCoursePlannerPath('pathways');
}

/**
 * Path for a single pathway (specialization) detail.
 * Embedded: GET /course-planner/pathways/:id
 * Direct:   GET /pathways/district/{d}/highschool/{h}/:id
 * Returns PathwayModel with nested requirements and requirementCourses.
 */
export function getPathwayDetailPath(pathwayId: string): string {
  return `${getCoursePlannerPath('pathways')}/${encodeURIComponent(pathwayId)}`;
}

/**
 * Path for highschools (schools list).
 * QA: /highschools (proxied through succeed-api). Local: /highschools (direct to service).
 */
export function getHighschoolsPath(): string {
  if (isDirectApiMode()) {
    return '/highschools';
  }
  return '/highschools';
}

/**
 * Path for a single student course plan (GET /student-course-plans/:id).
 * Embedded: /course-planner/student-course-plans/:id.
 * Direct:   /student-course-plans/district/{d}/highschool/{h}/:id.
 */
export function getStudentCoursePlanDetailPath(planId: string): string {
  return `${getCoursePlannerPath('student-course-plans')}/${planId}`;
}

/**
 * Data-ingest async job: update student course plans after a plan of study publish (CPUI parity).
 * Embedded: `/data-ingest/jobs/course-planner/update-pos-student-plans` on succeed-api.
 */
export function getDataIngestUpdatePosStudentPlansPath(planId?: string): string {
  const base = '/data-ingest/jobs/course-planner/update-pos-student-plans';
  return planId ? `${base}/${encodeURIComponent(planId)}` : base;
}

/**
 * Disables the legacy scheduled `update-pos-student-plans` ingress (matches POST schedule with `scheduleId`).
 * Embedded: `/data-ingest/jobs/course-planner/update-pos-student-plans/disable` on succeed-api.
 */
export function getDataIngestDisableScheduledUpdatePosStudentPlansPath(): string {
  return '/data-ingest/jobs/course-planner/update-pos-student-plans/disable';
}
